/**
 * Multi-selection action registry — single source of truth for the
 * action set surfaced by both MultiSelectionToolbar (Group +
 * Duplicate + Delete) and the right-click menu's `kind === 'multi'`
 * branch (Group + Copy + Duplicate + Delete).
 *
 * Multi actions take ARRAYS of ids, not single ids. Each entry's
 * run() forwards to ctx.on*Many with ctx.ids; the surface decides
 * which ids constitute "the selection."
 *
 * Group is the first entry — it's the "creative" gesture (creates a
 * new frame containing the selection) so it sits before the
 * copy/duplicate cluster. Group requires 2+ ids by definition;
 * other entries gate on length > 0.
 *
 * No locked-state at the registry layer — multi-selection treats
 * locked + unlocked targets uniformly here; per-target locked
 * filter is enforced inside each handler (e.g. onDeleteMany +
 * onGroupMany skip locked nodes internally). Multi actions are
 * unavailable when ids is empty (defensive — surfaces wouldn't
 * render in that state, but the registry should report it
 * honestly).
 */

export interface MultiActionContext {
  ids: string[];
  onCopyMany: (ids: string[]) => void;
  onDuplicateMany: (ids: string[]) => void;
  onDeleteMany: (ids: string[]) => void;
  onGroupMany: (ids: string[]) => void;
}

export interface MultiAction {
  id: 'group' | 'copy' | 'duplicate' | 'delete';
  label: string;
  icon: string;
  shortcut?: string;
  danger?: boolean;
  available: (ctx: MultiActionContext) => boolean;
  run: (ctx: MultiActionContext) => void;
}

export const MULTI_ACTIONS: MultiAction[] = [
  {
    id: 'group',
    label: 'Group',
    // crop_free reads as "draw a frame around this" — four corner
    // brackets defining a region. Matches what the action literally
    // does (creates a frame container around the selection).
    icon: 'crop_free',
    // Group needs 2+ targets — group of one is a no-op at the
    // hook level too. Distinct from the others which gate on >0.
    available: (c) => c.ids.length >= 2,
    run: (c) => c.onGroupMany(c.ids),
  },
  {
    id: 'copy',
    label: 'Copy',
    icon: 'content_copy',
    available: (c) => c.ids.length > 0,
    run: (c) => c.onCopyMany(c.ids),
  },
  {
    id: 'duplicate',
    label: 'Duplicate',
    icon: 'file_copy',
    available: (c) => c.ids.length > 0,
    run: (c) => c.onDuplicateMany(c.ids),
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: 'delete',
    danger: true,
    available: (c) => c.ids.length > 0,
    run: (c) => c.onDeleteMany(c.ids),
  },
];
